import { json } from '@codemirror/lang-json';
import { PlusIcon, Trash01Icon } from '@langchain/untitled-ui-icons';
import { FormControl, Input } from '@mui/joy';

import { Fragment, useRef, useState } from 'react';

import { Code } from '@/components/Code/Code';
import { Pane } from '@/components/Pane';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { SecretInput } from '@/components/SecretInput';
import { Button, IconButton, Text } from '@/design-system';

const SAMPLE_PAYLOAD = JSON.stringify(
  {
    rule_id: '969d3725-3ae1-5751-b486-a691e8ec0a4c',
    start_time: '2024-02-02T23:00:21.000000',
    end_time: '2024-02-02T23:01:00.000000',
    runs: [
      {
        id: '4af03983-c4dc-479b-bac1-9291bbc33107',
        trace_id: '4af03983-c4dc-479b-bac1-9291bbc33107',
        name: 'ChatOpenAI',
        inputs: {},
        run_type: 'llm',
        start_time: '2024-02-02T22:29:21.706000',
        end_time: '2024-02-02T22:29:22.656000',
        extra: {},
        error: null,
        serialized: {},
        outputs: {},
        parent_run_id: 'b3b1ea89-e43d-4a25-8cb9-9f826573b84e',
        manifest_id: null,
        manifest_s3_id: '7b17b632-d35c-5262-8fef-9e92bb8c09f3',
        events: [],
        tags: [],
        inputs_s3_urls: {},
        outputs_s3_urls: {},
        dotted_order: '...',
        status: 'success',
        child_run_ids: null,
        direct_child_run_ids: null,
        parent_run_ids: [],
        feedback_stats: null,
        reference_example_id: null,
        total_tokens: 150,
        prompt_tokens: 135,
        completion_tokens: 15,
        first_token_time: '2024-02-02T22:29:22.335000',
      },
    ],
  },
  null,
  2
);

function RulesWebhookHeadersControl(props: {
  value: Record<string, string> | undefined;
  onChange: (value: Record<string, string> | undefined) => void;
}) {
  const [innerValue, setInnerValue] = useState(
    Object.entries(props.value ?? {})
  );

  const onChange = (
    value: [key: string, value: string] | null,
    index?: number
  ) => {
    setInnerValue((prev) => {
      const newValue = prev.slice(0);
      if (index == null) {
        if (value != null) newValue.push(value);
      } else {
        if (value != null) {
          newValue[index] = value;
        } else {
          newValue.splice(index, 1);
        }
      }

      props.onChange(Object.fromEntries(newValue.filter((i) => i[0] !== '')));
      return newValue;
    });
  };

  const firstMount = useRef(false);
  if (!firstMount.current) {
    if (innerValue.length === 0) onChange(['', '']);
    firstMount.current = true;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-[1fr,1fr,auto] items-end gap-2">
        <Text variant="md" weight="medium">
          Name
        </Text>
        <Text variant="md" weight="medium">
          Value
        </Text>
        <span />

        {innerValue.map(([key, value], idx) => (
          <Fragment key={idx}>
            <FormControl required>
              <Input
                type="text"
                value={key}
                onChange={(e) => onChange([e.target.value, value], idx)}
                placeholder="Type name..."
                size="sm"
              />
            </FormControl>

            <FormControl>
              <SecretInput
                value={value}
                onChange={(e) => onChange([key, e.target.value], idx)}
                placeholder="Type value..."
                required
                size="sm"
              />
            </FormControl>

            <IconButton
              variant="outlined"
              color="secondary"
              size="md"
              onClick={() => onChange(null, idx)}
              icon={Trash01Icon}
              label="Delete"
            />
          </Fragment>
        ))}
      </div>
      <Button
        variant="outlined"
        color="secondary"
        leftDecorator={PlusIcon}
        onClick={() => onChange(['', ''])}
      >
        Add
      </Button>
    </div>
  );
}

function RulesWebhookHeaders(props: {
  value: Record<string, string> | undefined;
  onChange: (value: Record<string, string> | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const numHeaders = Object.keys(props.value ?? {}).length;
  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger>
        <Button variant="outlined" color="secondary" size="md">
          <Text variant="md">Headers</Text>
          {numHeaders > 0 && (
            <div className="flex items-center gap-1 rounded-sm border border-secondary bg-secondary px-1 py-px">
              <Text variant="xs">{numHeaders}</Text>
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="min-w-[500px]">
        {open && (
          <RulesWebhookHeadersControl
            value={props.value}
            onChange={props.onChange}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

export function RulesWebhookPane(props: {
  value: { url: string; headers?: Record<string, string> }[] | undefined;
  onChange: (
    value: { url: string; headers?: Record<string, string> }[]
  ) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  webhookToEditIndex?: number;
}) {
  const [value, setValue] = useState<
    { url: string; headers?: Record<string, string> }[]
  >(props.value ?? [{ url: '', headers: {} }]);
  const webhookToEditIndex = props.webhookToEditIndex ?? 0;

  return (
    <Pane
      title="Webhooks"
      open={props.open}
      onClose={() => props.setOpen(false)}
      dialogStyle={{
        marginLeft: 'calc(max(4.5rem, 100vw - max(20vw, 900px)))',
      }}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          props.onChange(value);
          props.setOpen(false);
        }}
      >
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">Endpoints</span>
          {value.map((item, index) => (
            <div
              key={index}
              className="grid grid-cols-[1fr,auto,auto] items-end gap-2"
            >
              <Input
                name="url"
                type="url"
                value={item.url}
                placeholder="https://..."
                onChange={(e) => {
                  const newValue = [...value];
                  newValue[index] = {
                    ...newValue[index],
                    url: e.target.value,
                  };
                  setValue(newValue);
                }}
                startDecorator={'POST'}
                size="sm"
                sx={{
                  paddingY: '5px',
                }}
                autoFocus={webhookToEditIndex === index}
              />

              <RulesWebhookHeaders
                value={item.headers}
                onChange={(newValue) => {
                  const newItems = [...value];
                  newItems[index] = {
                    ...newItems[index],
                    headers: newValue,
                  };
                  setValue(newItems);
                }}
              />

              <IconButton
                variant="outlined"
                color="secondary"
                size="md"
                onClick={() => setValue(value.filter((_, i) => i !== index))}
                icon={Trash01Icon}
                label="Delete"
              />
            </div>
          ))}

          <Button
            color="secondary"
            onClick={() => setValue([...value, { url: '' }])}
            leftDecorator={PlusIcon}
            className="self-start"
          >
            Endpoint
          </Button>
        </div>

        <div className="flex flex-col gap-2">
          <Text variant="md">Sample webhook payload</Text>

          <Code
            language={json()}
            readOnly
            showCopyButton
            variant="plain"
            fontSize="12px"
            value={SAMPLE_PAYLOAD}
          />
        </div>

        <div className="flex flex-row justify-end gap-2">
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={() => props.setOpen(false)}
            size="md"
          >
            Cancel
          </Button>
          <Button type="submit" size="md">
            Save
          </Button>
        </div>
      </form>
    </Pane>
  );
}
